import * as React from "react";
import * as THREE from "three";
import { extend, useFrame, useThree } from "@react-three/fiber";
import { shaderMaterial } from "@react-three/drei";

/**
 * Drop-in replacement for drei's <Sparkles> with a hardened shader.
 *
 * drei's version has two unguarded computations that poison float render
 * targets (the transition FBOs and EffectComposer buffers are HalfFloat, so
 * GPU blending there is NOT clamped to [0,1]):
 *
 *   1. `gl_PointSize *= 1.0 / -viewPosition.z` — explodes for points at/behind
 *      the camera plane.
 *   2. fragment alpha `0.05 / distanceToCenter - 0.1` — for a sub-pixel sprite
 *      the single covered fragment can sample gl_PointCoord at ~(0.5, 0.5),
 *      making distanceToCenter ≈ 0 and alpha huge/INF. Blending then writes
 *      large negative / NaN values into the float target, and Bloom's
 *      mipmapBlur smears them over the frame — visible as a near-fullscreen
 *      black flash with a single white dot, recurring every 2π/speed seconds
 *      as the sparkle drifts across the same sub-pixel alignment.
 *
 * Both are clamped here; drift formulas and defaults match drei exactly, so
 * the rendered look is unchanged. Props cover the subset this app uses
 * (uniform scalar size/speed/opacity and a single color).
 */
const SparklesSafeMaterial = shaderMaterial(
  {
    time: 0,
    pixelRatio: 1,
    size: 1,
    speed: 1,
    opacity: 1,
    color: new THREE.Color("#ffffff"),
  },
  /* glsl */ `
    uniform float pixelRatio;
    uniform float time;
    uniform float size;
    uniform float speed;
    void main() {
      vec4 modelPosition = modelMatrix * vec4(position, 1.0);
      modelPosition.y += sin(time * speed + modelPosition.x * 100.0) * 0.2;
      modelPosition.z += cos(time * speed + modelPosition.x * 100.0) * 0.2;
      modelPosition.x += cos(time * speed + modelPosition.x * 100.0) * 0.2;
      vec4 viewPosition = viewMatrix * modelPosition;
      gl_Position = projectionMatrix * viewPosition;
      float dist = -viewPosition.z;
      if (dist < 0.1) {
        // At or behind the camera plane: clip the point away entirely instead
        // of letting the size division blow up.
        gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
        gl_PointSize = 1.0;
      } else {
        gl_PointSize = min(size * 25.0 * pixelRatio / dist, 64.0);
      }
    }`,
  /* glsl */ `
    uniform vec3 color;
    uniform float opacity;
    void main() {
      float distanceToCenter = distance(gl_PointCoord, vec2(0.5));
      // clamp() is the actual bug fix — see the component docblock.
      float strength = clamp(0.05 / distanceToCenter - 0.1, 0.0, 1.0);
      gl_FragColor = vec4(color, strength * opacity);
      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }`,
);

extend({ SparklesSafeMaterial });

type SparklesProps = {
  /** Number of particles. */
  count?: number;
  /** Half-extent box the particles are spread across, per axis. */
  scale?: number | [number, number, number];
  /** Point size multiplier. */
  size?: number;
  /** Drift speed (rad/s of the sin/cos wiggle). */
  speed?: number;
  /** Peak sprite opacity. */
  opacity?: number;
  /** Sprite color. */
  color?: string;
};

export function Sparkles({
  count = 100,
  scale = 1,
  size = 1,
  speed = 1,
  opacity = 1,
  color = "#ffffff",
}: SparklesProps) {
  const materialRef = React.useRef<
    THREE.ShaderMaterial & { time: number }
  >(null);
  const dpr = useThree((state) => state.viewport.dpr);

  const extents = Array.isArray(scale) ? scale : [scale, scale, scale];
  const positions = React.useMemo(
    () =>
      Float32Array.from(
        Array.from({ length: count }, () =>
          extents.map(THREE.MathUtils.randFloatSpread),
        ).flat(),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, ...extents],
  );

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.time = state.clock.elapsedTime;
    }
  });

  return (
    <points key={`sparkles-${count}-${extents.join("-")}`}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      {/* @ts-expect-error sparklesSafeMaterial is extended via extend() */}
      <sparklesSafeMaterial
        ref={materialRef}
        transparent
        depthWrite={false}
        pixelRatio={dpr}
        size={size}
        speed={speed}
        opacity={opacity}
        color={color}
      />
    </points>
  );
}
